// Board camera (IMPLEMENTATION §2.9): CELL_WU world units per cell, zoom = cellCss·dprEff/CELL_WU, the mask's
// top-left world cell on layout.board. A relayout snaps; an expansion tweens from the current fit to the new
// one over render.expansionTweenMs (0 in ?test). Driven from the frame clock (no Phaser tween), so the probe
// and `idle()` can see whether it is moving.
import type { Layout } from '../../layout/computeLayout.ts';
import { type CameraFit, cameraFor, easeByName, lerpFit } from '../cameraMath.ts';
import { type PScene, setCamera } from './phaserCompat.ts';

export class BoardCamera {
  private cur: CameraFit | null = null;
  private from: CameraFit | null = null;
  private to: CameraFit | null = null;
  private t0 = 0;
  private dur = 0;
  private frozen = false;
  private tweenN = 0;
  private readonly cellWu: number;
  private readonly ease: string;

  /** `ease`: the juice.json easing name of the expansion tween (easing.out). */
  constructor(cellWu: number, ease: string) {
    this.cellWu = cellWu;
    this.ease = ease;
    easeByName(ease, 0); // an unknown name fails at boot, not mid-tween
  }

  /** Targets `layout`; `animateMs` > 0 tweens from where the camera is now. */
  fit(layout: Layout, dprEff: number, animateMs: number, nowMs: number): void {
    const target = cameraFor(layout, dprEff, this.cellWu);
    if (animateMs > 0 && this.cur !== null) {
      this.tweenN += 1;
      this.from = this.cur;
      this.to = target;
      this.t0 = nowMs;
      this.dur = animateMs;
    } else {
      this.from = null;
      this.to = null;
      this.cur = target;
    }
  }

  /** Advances the tween and applies the camera to `scene` (`shake`: an M10 offset in world units). */
  step(scene: PScene, nowMs: number, shake?: { x: number; y: number }): void {
    if (this.from !== null && this.to !== null && !this.frozen) {
      const t = this.dur > 0 ? (nowMs - this.t0) / this.dur : 1;
      if (t >= 1) {
        this.cur = this.to;
        this.from = null;
        this.to = null;
      } else {
        this.cur = lerpFit(this.from, this.to, easeByName(this.ease, t));
      }
    }
    if (this.cur !== null)
      setCamera(
        scene,
        this.cur.zoom,
        this.cur.scrollX + (shake?.x ?? 0),
        this.cur.scrollY + (shake?.y ?? 0),
      );
  }

  freeze(on: boolean): void {
    this.frozen = on;
  }

  /** Zoom tweens started (M10 reduce-motion spy: an expansion snaps under reduce motion). */
  get tweens(): number {
    return this.tweenN;
  }

  moving(): boolean {
    return this.to !== null;
  }

  current(): CameraFit | null {
    return this.cur;
  }
}
